import type { QueryClient } from '@tanstack/react-query';
import { type RouteObject } from 'react-router-dom';

import { firstAllowed, requirePermissions } from '@/app/requirePermissions';
import { PagePermissions } from '@/models/pagePermissions';
import { Permissions } from '@/models/permission';

const routes = (queryClient: QueryClient): RouteObject => ({
  path: 'store',
  loader: requirePermissions({ oneOf: PagePermissions.snapshots }),
  children: [
    {
      path: '',
      loader: firstAllowed([
        { to: 'snapshot/new', oneOf: [Permissions.store.create] },
        { to: 'snapshot', oneOf: PagePermissions.snapshots },
      ]),
    },
    {
      path: 'snapshot',
      lazy: () => import('./snapshot'),
      children: [
        {
          path: 'new',
          // The gate is composed inside lazy(): a static `loader` alongside a lazy
          // one is ignored by the router, which would drop the form's own loader.
          lazy: async () => {
            const { loader, Component } = await import('./snapshot/form');
            return {
              loader: requirePermissions(
                { oneOf: [Permissions.store.create] },
                loader(queryClient),
              ),
              Component,
            };
          },
        },
        {
          path: ':snapshotId',
          lazy: async () => {
            const { loader, Component } = await import('./snapshot/form');
            return { loader: loader(queryClient), Component };
          },
        },
      ],
    },
  ],
});

export const storeRoute = (queryClient: QueryClient): RouteObject =>
  import.meta.env.VITE_ADMIN_DISABLED_FEATURE.includes('store')
    ? {}
    : routes(queryClient);
